import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
  keyframes,
  SxProps,
  Theme,
} from '@mui/material';

export const ButtonVariants = {
  GRADIENT_PRIMARY: 'gradientPrimary',
  PREMIUM: 'premium',
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
  WHITE: 'white',
  PLAIN: 'plain',
  SUCCESS: 'success',
  FAILURE: 'failure',
  HIGHLIGHT: 'highlight',
  CUSTOM: 'custom',
} as const;

type BaseButtonProps = {
  onClick?: (e?: React.MouseEvent) => void;
  label?: string;
  variant?: (typeof ButtonVariants)[keyof typeof ButtonVariants];
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  skinny?: boolean;
  colorOverride?: string;
  sx?: SxProps<Theme>;
  type?: 'button' | 'submit' | 'reset';
  isLoading?: boolean;
  fileUploadProps?: React.InputHTMLAttributes<HTMLInputElement>;
  pauseAnimation?: boolean;
};

export type ButtonProps<T extends 'button' | 'a'> = BaseButtonProps &
  Omit<MuiButtonProps<T>, keyof BaseButtonProps | 'component' | 'loading'> & {
    component?: T;
  };

export const Button = <T extends 'button' | 'a' = 'button'>({
  onClick,
  label,
  variant = 'primary',
  startIcon = null,
  endIcon = null,
  disabled,
  fullWidth = true,
  skinny = true,
  colorOverride,
  sx,
  fileUploadProps,
  component,
  isLoading,
  pauseAnimation,
  ...props
}: ButtonProps<T>) => {
  startIcon = isLoading ? (
    <CircularProgress size={16} sx={{ color: 'inherit' }} thickness={6} />
  ) : (
    startIcon
  );

  const isFullBleed = variant === ButtonVariants.PLAIN;

  return (
    <MuiButton
      onClick={e => {
        onClick?.(e);
        e.currentTarget.blur();
      }}
      sx={[
        ({ typography }) => (skinny ? typography.button2 : typography.button1),
        {
          py: 1.5,
          px: 1.5,
          borderRadius: 6,
          textWrap: 'noWrap',
          textTransform: 'none',
          overflow: 'hidden',
          height: 32,
          '& .MuiSvgIcon-root': {
            fontSize: 16,
          },
          ...(label && startIcon ? { pl: 1.5, pr: 2 } : {}),
          transition:
            'background-color 0.2s, opacity 0.2s, color 0.2s, border-color 0.2s',
          '& .MuiButton-startIcon': {
            mr: skinny ? 0.5 : 1,
            ml: 0,
          },
          '& .MuiButton-endIcon': {
            mr: 0,
            ml: skinny ? 0.5 : 1,
          },
        },
        styles[variant],
        variant === ButtonVariants.HIGHLIGHT &&
          pauseAnimation && {
            animation: 'none',
            color: 'white',
            background: 'secondary.main',
          },
        colorOverride && getColorOverride(colorOverride),
        !fullWidth && { minWidth: 'fit-content' },
        !skinny && {
          height: 48,
          '& .MuiSvgIcon-root': {
            fontSize: 24,
          },
          px: 2,
          ...(label && startIcon ? { pl: 2, pr: 3 } : {}),
        },
        skinny && {
          height: 32,
          '& .MuiSvgIcon-root': {
            fontSize: 16,
          },
          px: 1.5,
          ...(label && startIcon ? { pl: 1.5, pr: 2 } : {}),
        },
        !label && {
          minWidth: 48,
          px: 0,
          '& .MuiButton-startIcon': {
            mr: 0,
          },
        },
        isFullBleed && { px: 0 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      variant={'contained'}
      startIcon={startIcon}
      endIcon={endIcon}
      disableRipple
      disableElevation
      disabled={disabled || isLoading}
      fullWidth={fullWidth}
      {...(fileUploadProps && { component: 'label' })}
      {...(props as ButtonProps<T>)}>
      {label}
      {fileUploadProps && <input hidden type="file" {...fileUploadProps} />}
    </MuiButton>
  );
};

const borderRotate = keyframes`
  from {
    --border-rotate-angle: 0deg;
  }

  to {
    --border-rotate-angle: 360deg;
  }
`;

const styles: Record<string, SxProps | ((arg: Theme) => SxProps)> = {
  [ButtonVariants.PRIMARY]: ({ palette }: Theme) => ({
    backgroundColor: 'primary.main',
    color: 'white',
    '&:hover, &:focus': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), ${palette.primary.active}`,
    },
    '&:active': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%), ${palette.primary.active}`,
      color: 'white',
    },
    '&:disabled': {
      backgroundColor: 'grey100',
    },
  }),
  [ButtonVariants.GRADIENT_PRIMARY]: ({ palette }: Theme) => ({
    background: palette.gradients.button,
    color: 'white',
    '&:hover, &:focus': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), ${palette.gradients.button}`,
    },
    '&:active': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%), ${palette.gradients.button}`,
      color: 'white',
    },
    '&:disabled': {
      background: 'grey100',
    },
  }),
  [ButtonVariants.PREMIUM]: ({ palette, customShadows }: Theme) => ({
    boxShadow: customShadows.button,
    background: palette.gradients.premium,
    color: 'black',
    '&:hover, &:focus': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), ${palette.gradients.premium}`,
      boxShadow: customShadows.button,
    },
    '&:active': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%), ${palette.gradients.premium}`,
      boxShadow: customShadows.button,
    },
    '&:disabled': {
      background: 'grey100',
      boxShadow: customShadows.button,
    },
  }),
  [ButtonVariants.SECONDARY]: ({ palette }: Theme) => ({
    backgroundColor: 'white',
    color: 'primary.main',
    border: `1px solid ${palette.primary.main}`,
    '&:hover, &:focus': {
      backgroundColor: 'grey50',
    },
    '&:active': {
      backgroundColor: 'grey50',
      color: 'secondary.active',
    },
    '&:disabled': {
      backgroundColor: 'grey100',
    },
  }),
  [ButtonVariants.TERTIARY]: {
    backgroundColor: 'transparent',
    color: 'text.main',
    '&:hover, &:focus': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
    },
    '&:active': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    '&:disabled': {
      backgroundColor: 'transparent',
      color: 'grey100',
    },
  },
  [ButtonVariants.WHITE]: ({ palette }: Theme) => ({
    backgroundColor: 'white',
    color: 'primary.main',
    '&:hover, &:focus': {
      backgroundColor: 'grey50',
      color: palette.primary.active,
    },
  }),
  [ButtonVariants.PLAIN]: {
    backgroundColor: 'transparent',
    borderRadius: 0,
    color: 'secondary.main',
    '&:hover, &:focus': {
      backgroundColor: 'transparent',
      color: 'secondary.active',
    },
    '&:active': {
      backgroundColor: 'transparent',
      color: 'secondary.active',
    },
    '&:disabled': {
      backgroundColor: 'transparent',
      color: 'grey100',
    },
  },
  [ButtonVariants.SUCCESS]: ({ palette }: Theme) => ({
    backgroundColor: 'green',
    color: 'white',
    '&:hover, &:focus': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), ${palette.green20}`,
    },
    '&:active': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%), ${palette.green20}`,
      color: 'white',
    },
    '&:disabled': {
      backgroundColor: 'grey100',
    },
  }),
  [ButtonVariants.FAILURE]: ({ palette }: Theme) => ({
    backgroundColor: 'red',
    color: 'white',
    '&:hover, &:focus': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), ${palette.salmon}`,
    },
    '&:active': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%), ${palette.salmon}`,
      color: 'white',
    },
    '&:disabled': {
      backgroundColor: 'grey100',
    },
  }),
  [ButtonVariants.HIGHLIGHT]: (theme: Theme) => ({
    color: 'secondary.main',
    animation: `2.5s ${borderRotate} linear infinite`,
    border: '3px solid #0000',
    background: `linear-gradient(white, white) padding-box, linear-gradient(var(--border-rotate-angle), ${theme.palette.secondary.main} 0%, white 50%) border-box`,
  }),
  [ButtonVariants.CUSTOM]: {},
};

const getColorOverride = (color: string) => ({
  color: color,
  borderColor: color,
  '&:hover, &:focus': {
    color: color,
    borderColor: color,
  },
});
